// Performance monitoring dashboard utilities
import { PerformanceLogger } from './performanceLogger';

export interface PerformanceMetrics {
  averageLoadTime: number;
  slowOperations: number;
  cacheHitRate: number;
  criticalOperations: number;
  totalOperations: number;
  timeframe: string;
}

export class PerformanceDashboard {
  private static alertThresholds = {
    critical: 3000,    // 3 seconds
    slow: 2000,        // 2 seconds
    warning: 1000,     // 1 second
    info: 500          // 500ms
  };

  static getMetrics(timeframeMins = 30): PerformanceMetrics {
    const logs = PerformanceLogger.getLogs();
    const cutoffTime = Date.now() - (timeframeMins * 60 * 1000);
    const recentLogs = logs.filter(log => log.timestamp > cutoffTime);

    if (recentLogs.length === 0) {
      return {
        averageLoadTime: 0,
        slowOperations: 0,
        cacheHitRate: 0,
        criticalOperations: 0,
        totalOperations: 0,
        timeframe: `${timeframeMins} minutes`
      };
    }

    const totalDuration = recentLogs.reduce((sum, log) => sum + log.duration, 0);
    const averageLoadTime = Math.round(totalDuration / recentLogs.length);
    
    const slowOperations = recentLogs.filter(log => 
      log.duration > this.alertThresholds.slow
    ).length;
    
    const criticalOperations = recentLogs.filter(log => 
      log.duration > this.alertThresholds.critical
    ).length;

    // Calculate cache hit rate from metadata
    const cacheOperations = recentLogs.filter(log => 
      log.metadata && typeof log.metadata.cached === 'boolean'
    );
    const cacheHits = cacheOperations.filter(log => log.metadata.cached).length;
    const cacheHitRate = cacheOperations.length > 0 
      ? Math.round((cacheHits / cacheOperations.length) * 100) 
      : 0;

    return {
      averageLoadTime,
      slowOperations,
      cacheHitRate,
      criticalOperations,
      totalOperations: recentLogs.length,
      timeframe: `${timeframeMins} minutes`
    };
  }

  static getOperationBreakdown(timeframeMins = 30) {
    const logs = PerformanceLogger.getLogs();
    const cutoffTime = Date.now() - (timeframeMins * 60 * 1000);
    const recentLogs = logs.filter(log => log.timestamp > cutoffTime);

    const breakdown = recentLogs.reduce((acc, log) => {
      if (!acc[log.operation]) {
        acc[log.operation] = {
          count: 0,
          totalDuration: 0,
          averageDuration: 0,
          maxDuration: 0,
          minDuration: Infinity
        };
      }
      
      const op = acc[log.operation];
      op.count++;
      op.totalDuration += log.duration;
      op.maxDuration = Math.max(op.maxDuration, log.duration);
      op.minDuration = Math.min(op.minDuration, log.duration);
      op.averageDuration = Math.round(op.totalDuration / op.count);
      
      return acc;
    }, {} as Record<string, any>);

    return Object.entries(breakdown)
      .sort(([,a], [,b]) => b.averageDuration - a.averageDuration)
      .slice(0, 10); // Top 10 slowest operations
  }

  static generatePerformanceReport(): string {
    const metrics = this.getMetrics();
    const breakdown = this.getOperationBreakdown();

    let report = `
🚀 PERFORMANCE REPORT (${metrics.timeframe})
===============================================
📊 Overall Metrics:
  • Total Operations: ${metrics.totalOperations}
  • Average Load Time: ${metrics.averageLoadTime}ms
  • Cache Hit Rate: ${metrics.cacheHitRate}%
  • Slow Operations: ${metrics.slowOperations}
  • Critical Operations: ${metrics.criticalOperations}

`;

    if (breakdown.length > 0) {
      report += `🔍 Top Operations by Average Duration:\n`;
      breakdown.forEach(([operation, stats], index) => {
        report += `  ${index + 1}. ${operation}: ${stats.averageDuration}ms (${stats.count}x)\n`;
      });
    }

    if (metrics.criticalOperations > 0) {
      report += `\n🚨 CRITICAL PERFORMANCE ISSUES DETECTED!\n`;
      report += `${metrics.criticalOperations} operations took longer than 3 seconds.\n`;
    }

    return report;
  }

  static logPerformanceAlert(operation: string, duration: number, metadata?: any) {
    const level = this.getAlertLevel(duration);
    const message = `${this.getAlertIcon(level)} ${operation} took ${duration}ms`;
    
    if (level === 'critical') {
      console.error(message, metadata);
    } else if (level === 'slow') {
      console.warn(message, metadata);
    } else if (level === 'warning') {
      console.log(message, metadata);
    } else {
      console.info(message, metadata);
    }
  }

  private static getAlertLevel(duration: number): 'critical' | 'slow' | 'warning' | 'info' {
    if (duration > this.alertThresholds.critical) return 'critical';
    if (duration > this.alertThresholds.slow) return 'slow';
    if (duration > this.alertThresholds.warning) return 'warning';
    return 'info';
  }

  private static getAlertIcon(level: string): string {
    switch (level) {
      case 'critical': return '🚨';
      case 'slow': return '🐌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  }
}

// Auto-generate report every 5 minutes in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    const report = PerformanceDashboard.generatePerformanceReport();
    console.log(report);
  }, 5 * 60 * 1000);
}